import { supabase } from '../supabase';
import {
  CommercialProgram,
  CommercialProgramVersion,
  Commitment,
  CommitmentNote,
  ProgramDateBasis,
  ProgramUnit,
} from '../utils/types';

/**
 * Programa Comercial (ex Compromisos): un programa por exportadora, temporada
 * y especie, con sus compromisos por cliente y semana, versiones y notas.
 */

export const getCommercialProgram = async (
  exporterId: number,
  season: string,
  speciesId: number
): Promise<CommercialProgram | null> => {
  const { data, error } = await supabase
    .from('commercial_programs')
    .select('*')
    .eq('exporter_id', exporterId)
    .eq('season', season)
    .eq('species_id', speciesId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as CommercialProgram) || null;
};

export const createCommercialProgram = async (program: {
  exporter_id: number;
  season: string;
  species_id: number;
  unit: ProgramUnit;
  date_basis: ProgramDateBasis;
  kg_per_ceq: number;
  kg_per_shipment: number | null;
  created_by?: number | null;
}): Promise<CommercialProgram> => {
  const { data, error } = await supabase
    .from('commercial_programs')
    .insert(program)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as CommercialProgram;
};

/**
 * Equivalencias. La unidad y la fecha base se cambian con changeProgramUnit
 * y changeProgramDateBasis, que dejan una versión.
 */
export const updateCommercialProgram = async (
  id: number,
  changes: Partial<Pick<CommercialProgram, 'kg_per_ceq' | 'kg_per_shipment'>>
): Promise<CommercialProgram> => {
  const { data, error } = await supabase
    .from('commercial_programs')
    .update({ ...changes, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as CommercialProgram;
};

/**
 * Cambia la unidad: guarda una versión con el programa como estaba y
 * convierte los compromisos vigentes con las equivalencias.
 */
export const changeProgramUnit = async (
  id: number,
  unit: ProgramUnit,
  kgPerCeq: number,
  kgPerShipment: number | null
): Promise<void> => {
  const { error } = await supabase.rpc('change_commercial_program_unit', {
    p_program_id: id,
    p_unit: unit,
    p_kg_per_ceq: kgPerCeq,
    p_kg_per_shipment: kgPerShipment,
  });
  if (error) throw new Error(error.message);
};

/** Cambia la fecha base (ETA/ETD) guardando antes una versión. */
export const changeProgramDateBasis = async (
  id: number,
  dateBasis: ProgramDateBasis
): Promise<void> => {
  const { error } = await supabase.rpc('change_commercial_program_date_basis', {
    p_program_id: id,
    p_date_basis: dateBasis,
  });
  if (error) throw new Error(error.message);
};

/** Todas las filas del programa, también las anteriores (historial). */
export const getProgramCommitments = async (
  programId: number
): Promise<Commitment[]> => {
  const { data, error } = await supabase
    .from('commitments')
    .select('*')
    .eq('program_id', programId)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data || []) as Commitment[];
};

export interface CommitmentChange {
  exporter_receiver_id: number;
  week: number;
  /** null o 0 = sin compromiso. */
  quantity: number | null;
}

export interface CommitmentChangeResult {
  created: number;
  updated: number;
  deleted: number;
  unchanged: number;
}

/** Guarda varias celdas en una transacción, dejando historial. */
export const setCommitments = async (
  programId: number,
  items: CommitmentChange[]
): Promise<CommitmentChangeResult> => {
  const { data, error } = await supabase.rpc('set_commitments', {
    p_program_id: programId,
    p_items: items,
  });
  if (error) throw new Error(error.message);
  return data as CommitmentChangeResult;
};

export const getProgramVersions = async (
  programId: number
): Promise<CommercialProgramVersion[]> => {
  const { data, error } = await supabase
    .from('commercial_program_versions')
    .select('*')
    .eq('program_id', programId)
    .is('deleted_at', null)
    .order('as_of', { ascending: true });
  if (error) throw new Error(error.message);
  return (data || []) as CommercialProgramVersion[];
};

export const createProgramVersion = async (version: {
  program_id: number;
  name: string;
  as_of?: string;
  date_basis?: ProgramDateBasis;
  created_by?: number | null;
}): Promise<CommercialProgramVersion> => {
  const { data, error } = await supabase
    .from('commercial_program_versions')
    .insert(version)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as CommercialProgramVersion;
};

export const renameProgramVersion = async (
  id: number,
  name: string
): Promise<void> => {
  const { error } = await supabase
    .from('commercial_program_versions')
    .update({ name })
    .eq('id', id);
  if (error) throw new Error(error.message);
};

export const deleteProgramVersion = async (id: number): Promise<void> => {
  const { error } = await supabase
    .from('commercial_program_versions')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
};

export const getCommitmentNotes = async (
  programId: number
): Promise<CommitmentNote[]> => {
  const { data, error } = await supabase
    .from('commitment_notes')
    .select('*, author:users!commitment_notes_created_by_fkey(first_name, last_name)')
    .eq('program_id', programId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return ((data || []) as (CommitmentNote & {
    author: { first_name: string | null; last_name: string | null } | null;
  })[]).map((note) => ({
    ...note,
    author:
      [note.author?.first_name, note.author?.last_name].filter(Boolean).join(' ') || null,
  }));
};

export const addCommitmentNote = async (note: {
  program_id: number;
  exporter_receiver_id: number;
  week: number | null;
  content: string;
  created_by?: number | null;
}): Promise<void> => {
  const { error } = await supabase.from('commitment_notes').insert(note);
  if (error) throw new Error(error.message);
};

export const deleteCommitmentNote = async (id: number): Promise<void> => {
  const { error } = await supabase
    .from('commitment_notes')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
};
